export const PERMISSION_ACTIONS = ['read', 'create', 'update', 'delete'];

export const PERMISSION_MODULES = [
  { key: 'dashboard', label: 'Dashboard', description: 'Analytics overview and KPIs.' },
  { key: 'bookings', label: 'Bookings', description: 'Trips, bookings, and dispatch.' },
  { key: 'users', label: 'Users', description: 'People management and access.' },
  { key: 'partners', label: 'Partners', description: 'Partner pages and branding.' },
  { key: 'fleet', label: 'Fleet', description: 'Vehicles, car types, approvals.' },
  { key: 'reports', label: 'Reports', description: 'Earnings, shift data, insights.' },
  { key: 'finance', label: 'Finance', description: 'Wallets, payouts, payments.' },
  { key: 'notifications', label: 'Notifications', description: 'Push notifications and alerts.' },
  { key: 'settings', label: 'Settings', description: 'System configuration and admin tools.' },
];

export const ROLE_DEFINITIONS = [
  { key: 'admin', label: 'Admin', description: 'Full system control.' },
  { key: 'supervisor', label: 'Supervisor', description: 'Operational oversight with limited deletes.' },
  { key: 'partner', label: 'Partner', description: 'Partner view with limited access.' },
  { key: 'partner_manager', label: 'Partner manager', description: 'Manage partner pages and bookings.' },
  { key: 'fleet_owner', label: 'Fleet owner', description: 'Own fleet operations and staff.' },
  { key: 'fleet_manager', label: 'Fleet manager', description: 'Manage fleet day-to-day.' },
  { key: 'taxi_owner', label: 'Taxi owner', description: 'Taxi business owner.' },
  { key: 'taxi_driver', label: 'Taxi driver', description: 'Driver access only.' },
  { key: 'customer', label: 'Customer', description: 'Customer access.' },
];

export const ROLE_SCOPES = {
  admin: 'all',
  supervisor: 'all',
  partner: 'partner',
  partner_manager: 'partner',
  fleet_owner: 'fleet',
  fleet_manager: 'fleet',
  taxi_owner: 'fleet',
  taxi_driver: 'self',
  customer: 'self',
};

const allowAll = (modules) =>
  modules.reduce((acc, module) => {
    acc[module.key] = PERMISSION_ACTIONS.reduce((actions, action) => {
      actions[action] = true;
      return actions;
    }, {});
    return acc;
  }, {});

const allowReadOnly = (modules) =>
  modules.reduce((acc, module) => {
    acc[module.key] = PERMISSION_ACTIONS.reduce((actions, action) => {
      actions[action] = action === 'read';
      return actions;
    }, {});
    return acc;
  }, {});

export const DEFAULT_ROLE_PERMISSIONS = (() => {
  const modules = PERMISSION_MODULES;
  const baseRead = allowReadOnly(modules);
  const adminAll = allowAll(modules);

  const supervisor = {
    ...adminAll,
    finance: { ...adminAll.finance, delete: false },
    settings: { ...adminAll.settings, delete: false },
    users: { ...adminAll.users, delete: false },
  };

  const partner = {
    ...baseRead,
    partners: { read: true, create: false, update: false, delete: false },
    bookings: { read: true, create: true, update: false, delete: false },
  };

  const partnerManager = {
    ...partner,
    partners: { read: true, create: false, update: true, delete: false },
    bookings: { read: true, create: true, update: true, delete: false },
    reports: { read: true, create: false, update: false, delete: false },
  };

  const fleetOwner = {
    ...baseRead,
    fleet: { read: true, create: true, update: true, delete: false },
    users: { read: true, create: true, update: true, delete: false },
    bookings: { read: true, create: true, update: true, delete: false },
    reports: { read: true, create: false, update: false, delete: false },
  };

  const fleetManager = {
    ...fleetOwner,
    finance: { read: true, create: false, update: false, delete: false },
  };

  const taxiOwner = {
    ...fleetOwner,
    partners: { read: true, create: false, update: false, delete: false },
  };

  const taxiDriver = {
    ...baseRead,
    bookings: { read: true, create: false, update: true, delete: false },
    fleet: { read: true, create: false, update: false, delete: false },
  };

  const customer = {
    ...baseRead,
    bookings: { read: true, create: true, update: true, delete: false },
  };

  return {
    admin: adminAll,
    supervisor,
    partner,
    partner_manager: partnerManager,
    fleet_owner: fleetOwner,
    fleet_manager: fleetManager,
    taxi_owner: taxiOwner,
    taxi_driver: taxiDriver,
    customer,
  };
})();

export const getUserRole = (profile) => {
  // Some reducers set profile to null while auth is resolving.
  const safeProfile = profile || {};
  const explicit = safeProfile.accessRole || safeProfile.role || safeProfile.userRole;
  if (explicit) return explicit;
  const base = safeProfile.usertype;
  if (base === 'admin') return 'admin';
  if (base === 'fleetadmin') return 'fleet_manager';
  if (base === 'driver') return 'taxi_driver';
  if (base === 'customer') return 'customer';
  return base || 'customer';
};

export const normalizePermissions = (permissions = {}) => {
  const normalized = {};
  ROLE_DEFINITIONS.forEach((role) => {
    const roleKey = role.key;
    normalized[roleKey] = {};
    PERMISSION_MODULES.forEach((module) => {
      const moduleKey = module.key;
      const source = permissions?.[roleKey]?.[moduleKey] || {};
      normalized[roleKey][moduleKey] = PERMISSION_ACTIONS.reduce((acc, action) => {
        acc[action] = Boolean(source[action]);
        return acc;
      }, {});
    });
  });
  return normalized;
};

export const getRolePermissions = (settings, roleKey) => {
  const configured = settings?.rolePermissions;
  const normalized = configured ? normalizePermissions(configured) : DEFAULT_ROLE_PERMISSIONS;
  return normalized[roleKey] || DEFAULT_ROLE_PERMISSIONS.customer;
};

export const getRoleScope = (settings, roleKey) => {
  return settings?.roleScopes?.[roleKey] || ROLE_SCOPES[roleKey] || 'self';
};

export const getUserScope = (profile, settings) => {
  return profile?.dataScope || getRoleScope(settings, getUserRole(profile));
};

export const mergeUserPermissions = (settings, profile) => {
  const roleKey = getUserRole(profile);
  const base = getRolePermissions(settings, roleKey);
  const overrides = profile?.permissions || {};
  const merged = { ...base };
  Object.keys(overrides).forEach((moduleKey) => {
    merged[moduleKey] = { ...base[moduleKey], ...overrides[moduleKey] };
  });
  return merged;
};

export const hasPermission = (profile, settings, moduleKey, action = 'read') => {
  if (!moduleKey) return true;
  const permissions = mergeUserPermissions(settings, profile);
  const modulePerms = permissions?.[moduleKey];
  if (!modulePerms) return false;
  return Boolean(modulePerms[action]);
};

export const hasAnyPermission = (profile, settings, moduleKey) => {
  const permissions = mergeUserPermissions(settings, profile);
  const modulePerms = permissions?.[moduleKey];
  if (!modulePerms) return false;
  return PERMISSION_ACTIONS.some((action) => modulePerms[action]);
};

export const getAllowedPartners = (profile) => {
  if (!profile) return [];
  if (Array.isArray(profile.allowedPartners) && profile.allowedPartners.length) {
    return profile.allowedPartners;
  }
  if (profile.partnerSlug) return [profile.partnerSlug];
  if (profile.partnerCode) return [profile.partnerCode];
  return [];
};
